import { useState } from "react";
import { Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { Panel, Th, Td, btn } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { NonEdiForm, useNonEdiData } from "@/components/sap/NonEdiForm";
import { AmazonBulkPanel } from "@/components/sap/AmazonBulkPanel";
import { FixButton } from "@/components/sap/FixDialog";

const template = async () => {
  try {
    const r = await api.get("/manual-orders/template", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = "Manual_Order_Entry_Template.xlsx";
    a.click();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

const Results = ({ rows, recheck }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="border-b border-slate-800"><tr><Th>PO</Th><Th>Customer</Th><Th>Delivery point</Th><Th>Lines</Th><Th>Qty</Th><Th>Result</Th></tr></thead>
      <tbody className="divide-y divide-slate-800/70">
        {rows.map((r, i) => (
          <tr key={i} data-testid={`noedi-upload-row-${r.po_number || i}`}>
            <Td className="font-mono">{r.po_number}</Td><Td>{r.partner}</Td><Td className="font-mono">{r.depot}</Td><Td>{r.lines}</Td><Td>{r.qty}</Td>
            <Td>{r.errors.length ? <><span className="text-xs text-rose-300">{r.errors.join("; ")}</span> <FixButton fix={r.fix && { ...r.fix, source: `spreadsheet PO ${r.po_number}` }} onDone={recheck} small testId={`noedi-fix-${r.po_number || i}`} /></>
              : r.order_id ? <span className="text-xs text-green-300">{r.idoc_file ? `Created - ${r.idoc_file}` : `Created - IDoc not made: ${r.error}`}</span>
              : <StatusBadge status="processed" label="Ready" />}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const UploadPanel = () => {
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const send = async (commit, f = file) => {
    const fd = new FormData();
    fd.append("file", f);
    fd.append("commit", commit ? "true" : "false");
    try {
      const { data } = await api.post("/manual-orders/upload", fd);
      setRes(data);
      if (commit) toast.success(`${data.orders.filter((x) => x.order_id).length} order(s) created`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) { setFile(f); send(false, f); }
  };
  const ok = res?.orders.filter((x) => !x.errors.length).length || 0;
  return (
    <Panel title="Upload orders from a spreadsheet" testId="noedi-upload">
      <div className="flex flex-wrap items-center gap-3 p-5">
        <Button variant="ghost" onClick={template} className={btn.ghost} data-testid="noedi-template-btn"><Download className="mr-1.5 h-4 w-4" /> Download template</Button>
        <label className={`inline-flex h-9 cursor-pointer items-center rounded-md px-3 text-sm ${btn.ghost}`} data-testid="noedi-upload-label">
          <Upload className="mr-1.5 h-4 w-4" /> Choose filled template
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={pick} data-testid="noedi-upload-input" />
        </label>
        <span className="text-xs text-slate-500">One row per order line. Rows with the same customer and PO become one order. The template also lists your customers, depots and items.</span>
        {res && !res.committed && ok > 0 && <Button onClick={() => send(true)} className={`ml-auto ${btn.primary}`} data-testid="noedi-commit-btn">Create {ok} order(s) and SAP IDocs</Button>}
      </div>
      {res && <Results rows={res.orders} recheck={() => send(false)} />}
    </Panel>
  );
};

export const NonEdiOrders = () => {
  const { partners, items, reload } = useNonEdiData();
  return (
    <div className="space-y-4" data-testid="noedi-tab">
      <p className="text-sm text-slate-400">For customers not yet on EDI. Set the customer up on <span className="text-slate-200">Trading Partners</span> with SAP IDoc switched on and its delivery points. Orders entered here are recorded in the hub and written as a SAP IDoc only - nothing is sent to Group O.</p>
      {partners.length === 0 && <p className="text-sm text-amber-300" data-testid="noedi-no-partners">No customer has SAP IDoc switched on yet.</p>}
      <AmazonBulkPanel />
      <NonEdiForm partners={partners} items={items} reload={reload} />
      <UploadPanel />
    </div>
  );
};
